import { createTheme } from "@mui/material/styles";
import { colors, radii, typography, elevation } from "./tokens";

export { colors, radii, typography, elevation } from "./tokens";
export { space, motion } from "./tokens";

/**
 * Catalog app theme.
 *
 * Maps the Softfone mobile design language onto MUI v7. The split is:
 *  - `primary`   → action blue (filled CTAs, links, focus rings)
 *  - `secondary` → brand purple (identity, avatars, brand badges)
 *
 * Component default overrides flatten MUI's stock elevation so cards,
 * dialogs, and the app bar sit calmly on the gray canvas as the guide
 * requires (§5.2, §10).
 *
 * Note: MUI's spacing multiplier stays at the default 8 so existing
 * `sx={{ p: 2 }}` usages keep their current 16px value. The 4pt grid
 * still applies — just use even multipliers (or the `space` token).
 */
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: colors.action.blue,
      light: colors.action.blueTint,
      dark: colors.action.blueHover,
      contrastText: colors.text.onAccent,
    },
    secondary: {
      main: colors.brand.purple,
      light: colors.brand.purpleTint,
      contrastText: colors.text.onAccent,
    },
    success: { main: colors.status.success, contrastText: "#fff" },
    error: { main: colors.status.danger, contrastText: "#fff" },
    warning: { main: colors.status.warning, contrastText: "#fff" },
    info: { main: colors.action.blue, contrastText: "#fff" },
    background: {
      default: colors.bg.canvas,
      paper: colors.bg.surface,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.tertiary,
    },
    divider: colors.border.hairline,
    grey: {
      50: "#FAFAFC",
      100: colors.bg.surfaceMuted,
      200: colors.border.hairline,
      300: "#D9D9DE",
      400: colors.text.tertiary,
      500: colors.text.secondary,
    },
  },
  shape: { borderRadius: radii.input },
  typography: {
    fontFamily: typography.fontFamily,
    fontWeightRegular: typography.weight.regular,
    fontWeightMedium: typography.weight.semibold,
    fontWeightBold: typography.weight.semibold,
    h1: { fontWeight: typography.weight.semibold, letterSpacing: 0 },
    h2: { fontWeight: typography.weight.semibold, letterSpacing: 0 },
    h3: { fontWeight: typography.weight.semibold, letterSpacing: 0 },
    h4: {
      fontWeight: typography.weight.semibold,
      fontSize: "1.5rem",
      letterSpacing: 0,
    },
    h5: {
      fontWeight: typography.weight.semibold,
      fontSize: "1.25rem",
      letterSpacing: 0,
    },
    h6: {
      fontWeight: typography.weight.semibold,
      fontSize: "1.0625rem",
      letterSpacing: 0,
    },
    subtitle1: {
      fontWeight: typography.weight.semibold,
      fontSize: "1.0625rem",
      letterSpacing: 0,
    },
    subtitle2: {
      fontWeight: typography.weight.semibold,
      fontSize: "0.875rem",
      letterSpacing: 0,
    },
    body1: { fontSize: "1rem", lineHeight: 1.5 },
    body2: { fontSize: "0.875rem", lineHeight: 1.5 },
    caption: { fontSize: "0.8125rem", color: colors.text.secondary },
    overline: { letterSpacing: 0, textTransform: "none" },
    button: {
      fontWeight: typography.weight.semibold,
      textTransform: "none",
      letterSpacing: 0,
    },
  },
  transitions: {
    duration: {
      shortest: 80,
      shorter: 150,
      short: 200,
      standard: 250,
      complex: 300,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bg.canvas,
          color: colors.text.primary,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        "*, *::before, *::after": {
          boxSizing: "border-box",
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none" },
        outlined: { borderColor: colors.border.hairline },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: "outlined" },
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          borderColor: colors.border.hairline,
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: {
          backgroundColor: colors.bg.surface,
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.border.hairline}`,
          backgroundImage: "none",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.bg.surface,
          borderColor: colors.border.hairline,
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radii.button,
          textTransform: "none",
          fontWeight: typography.weight.semibold,
          paddingInline: 16,
          paddingBlock: 8,
          minHeight: 40,
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: "0.875rem" },
        sizeLarge: {
          minHeight: 50,
          paddingInline: 24,
          fontSize: "1.0625rem",
        },
        outlined: {
          borderColor: colors.border.hairline,
          color: colors.text.primary,
          "&:hover": {
            borderColor: colors.text.secondary,
            backgroundColor: colors.bg.surfaceMuted,
          },
        },
        text: {
          color: colors.action.blue,
          "&:hover": { backgroundColor: colors.action.blueTint },
        },
        containedPrimary: {
          backgroundColor: colors.action.blue,
          "&:hover": { backgroundColor: colors.action.blueHover },
        },
        containedSecondary: {
          backgroundColor: colors.brand.purple,
          "&:hover": { backgroundColor: "#5A3FE0" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.text.primary,
          borderRadius: radii.input,
          "&:hover": { backgroundColor: colors.bg.surfaceMuted },
        },
        sizeSmall: { padding: 6 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.input,
          backgroundColor: colors.bg.surface,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.border.hairline,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.text.tertiary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.action.blue,
            borderWidth: 1.5,
          },
        },
        input: {
          paddingBlock: 10,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.text.secondary,
          "&.Mui-focused": { color: colors.action.blue },
        },
      },
    },
    MuiChip: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          borderRadius: radii.chip,
          fontWeight: typography.weight.semibold,
          fontSize: "0.75rem",
          height: 24,
        },
        outlined: { borderColor: colors.border.hairline },
        filled: {
          backgroundColor: colors.bg.surfaceMuted,
          color: colors.text.primary,
        },
      },
    },
    MuiDialog: {
      defaultProps: { PaperProps: { elevation: 0 } },
      styleOverrides: {
        paper: {
          borderRadius: radii.card,
          border: `1px solid ${colors.border.hairline}`,
          backgroundImage: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: typography.weight.semibold,
          fontSize: "1.0625rem",
          padding: "20px 24px 12px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "0 24px 16px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: "12px 24px 20px" },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.border.hairline },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          "&.Mui-selected": {
            backgroundColor: colors.bg.surfaceMuted,
            "&:hover": { backgroundColor: colors.bg.surfaceMuted },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: colors.border.hairline,
          fontSize: "0.9375rem",
          padding: "14px 16px",
        },
        head: {
          fontWeight: typography.weight.semibold,
          color: colors.text.secondary,
          backgroundColor: colors.bg.surface,
          fontSize: "0.8125rem",
          textTransform: "none",
          letterSpacing: 0,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-of-type .MuiTableCell-body": { borderBottom: 0 },
        },
      },
    },
    MuiAlert: {
      defaultProps: { variant: "standard" },
      styleOverrides: {
        root: { borderRadius: radii.input },
        standardError: {
          backgroundColor: "#FFECEB",
          color: "#7A1E18",
        },
        standardSuccess: {
          backgroundColor: "#E6F6EA",
          color: "#1B5E28",
        },
        standardWarning: {
          backgroundColor: "#FFF1DB",
          color: "#7A4A05",
        },
        standardInfo: {
          backgroundColor: colors.action.blueTint,
          color: colors.action.blueHover,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 24,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 2,
            "&.Mui-checked": {
              transform: "translateX(18px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: colors.status.success,
                opacity: 1,
              },
            },
          },
          "& .MuiSwitch-thumb": {
            width: 20,
            height: 20,
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          },
          "& .MuiSwitch-track": {
            backgroundColor: "#E9E9EB",
            opacity: 1,
            borderRadius: 12,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "rgba(11,11,15,0.92)",
          color: "#fff",
          fontSize: "0.75rem",
          paddingInline: 10,
          paddingBlock: 6,
          borderRadius: 8,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radii.input,
          border: `1px solid ${colors.border.hairline}`,
          boxShadow: elevation.floating,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.9375rem",
          paddingBlock: 8,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: colors.text.tertiary },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 4 },
      },
    },
  },
});

export default theme;
